import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ObjectForm, { ObjectFieldSpec } from "./ObjectForm";

type Row = { key: string; value: unknown };
type MenuItem = { label: string; path: string };

const OBJECT_FIELD_CONFIG: Record<
  string,
  { title: string; fields: ObjectFieldSpec[] }
> = {
  hero: {
    title: "Velkomstseksjon (øverst på forsiden)",
    fields: [
      { key: "welcome", label: "Velkomsttekst", type: "text" },
      { key: "clinicName", label: "Klinikknavn", type: "text" },
    ],
  },
  home: {
    title: "Forsiden – ingress",
    fields: [{ key: "intro", label: "Ingresstekst", type: "multiline" }],
  },
  aboutPage: {
    title: "«Om oss»-siden",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "paragraphs", label: "Avsnitt", type: "stringList" },
    ],
  },
  treatmentsPage: {
    title: "«Behandlingstilbud»-siden",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "description", label: "Beskrivelse", type: "multiline" },
    ],
  },
  contactPage: {
    title: "Kontakt-seksjonen",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "openingHoursLabel", label: "Etikett – åpningstider", type: "text" },
      { key: "openingHours", label: "Åpningstider", type: "text" },
      { key: "phoneHoursLabel", label: "Etikett – telefontid", type: "text" },
      { key: "phoneHours", label: "Telefontid", type: "text" },
      { key: "phoneDisplay", label: "Telefonnummer (som vises)", type: "text" },
      {
        key: "phoneHref",
        label: "Telefonnummer (lenke, f.eks. tel:+4751883500)",
        type: "text",
      },
      { key: "emailLabel", label: "Etikett – e-post", type: "text" },
      { key: "email", label: "E-postadresse", type: "text" },
      { key: "scheduleNote", label: "Fotnote om avvik i ferier", type: "text" },
      { key: "mapEmbedUrl", label: "Kart – innebygd URL", type: "text" },
      {
        key: "entrancePhoto",
        label: "Bilde av inngangen (sti under public/)",
        type: "text",
      },
    ],
  },
  hearingAidPage: {
    title: "Høreapparat-siden",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "paragraphs", label: "Avsnitt", type: "stringList" },
      { key: "supplierIntro", label: "Innledning til leverandørlisten", type: "text" },
    ],
  },
  staffPage: {
    title: "Ansatte-siden",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "description", label: "Beskrivelse", type: "multiline" },
    ],
  },
  appointmentPage: {
    title: "Timeavtale-siden",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "description", label: "Beskrivelse", type: "multiline" },
    ],
  },
  letter: {
    title: "Innkallingsbrev",
    fields: [
      { key: "title", label: "Tittel", type: "text" },
      { key: "subtitle", label: "Undertittel", type: "text" },
      { key: "clinicName", label: "Klinikknavn", type: "text" },
      { key: "address", label: "Adresse", type: "multiline" },
      { key: "phone", label: "Telefon", type: "text" },
      { key: "website", label: "Nettside", type: "text" },
      { key: "intro", label: "Innledning", type: "multiline" },
      { key: "location", label: "Beliggenhet", type: "multiline" },
      { key: "parking", label: "Parkering", type: "multiline" },
      { key: "allergy", label: "Info om allergitabletter", type: "multiline" },
      { key: "payment", label: "Betaling", type: "multiline" },
      { key: "cancel", label: "Avbestilling", type: "multiline" },
      { key: "infection", label: "Smittevern", type: "multiline" },
      { key: "closing", label: "Avslutningshilsen", type: "text" },
      { key: "regards", label: "Hilsen", type: "text" },
      { key: "signature", label: "Signatur (navn)", type: "text" },
      { key: "clinicClosing", label: "Klinikknavn (bunntekst)", type: "text" },
    ],
  },
};

export default function SiteContentAdmin() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/admin/site-content", { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            res.status === 401
              ? "Du er ikke lenger innlogget."
              : "Kunne ikke laste sidetekster."
          );
        }
        return res.json();
      })
      .then(setRows)
      .catch((err) =>
        setLoadError(
          err instanceof Error ? err.message : "Kunne ikke laste sidetekster."
        )
      );
  }, []);

  const save = async (key: string, value: unknown) => {
    const res = await fetch(`/api/admin/site-content/${key}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) {
      throw new Error("Save failed");
    }
    setRows((prev) =>
      prev ? prev.map((r) => (r.key === key ? { ...r, value } : r)) : prev
    );
  };

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  if (!rows) {
    return <Typography>Laster…</Typography>;
  }

  const byKey = Object.fromEntries(rows.map((r) => [r.key, r.value]));

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Sidetekster
      </Typography>
      <Stack spacing={3}>
        <MenuItemsForm
          value={((byKey.header as { menuItems?: MenuItem[] })?.menuItems ?? []) as MenuItem[]}
          onSave={(menuItems) => save("header", { menuItems })}
        />
        <TreatmentLinkInfoForm
          value={(byKey.treatmentLinkInfo as string) ?? ""}
          onSave={(value) => save("treatmentLinkInfo", value)}
        />
        {Object.entries(OBJECT_FIELD_CONFIG).map(([key, config]) => (
          <ObjectForm
            key={key}
            title={config.title}
            fields={config.fields}
            value={(byKey[key] as Record<string, any>) ?? {}}
            onSave={(value) => save(key, value)}
          />
        ))}
      </Stack>
    </Box>
  );
}

function MenuItemsForm({
  value,
  onSave,
}: {
  value: MenuItem[];
  onSave: (items: MenuItem[]) => Promise<void>;
}) {
  const [items, setItems] = useState<MenuItem[]>(value);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [saving, setSaving] = useState(false);

  useEffect(() => setItems(value), [value]);

  const update = (index: number, field: keyof MenuItem, fieldValue: string) => {
    const next = [...items];
    next[index] = { ...next[index], [field]: fieldValue };
    setItems(next);
    setStatus("idle");
  };

  const remove = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    setStatus("idle");
  };

  const add = () => setItems([...items, { label: "", path: "" }]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(items);
      setStatus("saved");
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Menyvalg (toppmenyen)
      </Typography>
      {status === "saved" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Lagret.
        </Alert>
      )}
      {status === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Lagring feilet.
        </Alert>
      )}
      {items.map((item, index) => (
        <Box key={index} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            label="Tekst"
            value={item.label}
            onChange={(e) => update(index, "label", e.target.value)}
            size="small"
          />
          <TextField
            label="Lenke (sti, f.eks. /treatments eller #contact)"
            value={item.path}
            onChange={(e) => update(index, "path", e.target.value)}
            size="small"
          />
          <IconButton size="small" onClick={() => remove(index)} aria-label="Fjern">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ mb: 2 }}>
        Legg til menyvalg
      </Button>
      <Box>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Lagre
        </Button>
      </Box>
    </Paper>
  );
}

function TreatmentLinkInfoForm({
  value,
  onSave,
}: {
  value: string;
  onSave: (v: string) => Promise<void>;
}) {
  const [text, setText] = useState(value);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const [saving, setSaving] = useState(false);

  useEffect(() => setText(value), [value]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(text);
      setStatus("saved");
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Lenketekst – informasjonsvideo (søvnbehandling)
      </Typography>
      {status === "saved" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Lagret.
        </Alert>
      )}
      {status === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Lagring feilet.
        </Alert>
      )}
      <TextField
        label="Lenketekst"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setStatus("idle");
        }}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button variant="contained" onClick={handleSave} disabled={saving}>
        Lagre
      </Button>
    </Paper>
  );
}
